<template>
    <div class="lg:tw-relative">
        <AcquisitionFormProgress
            :total-steps="2"
            :step="2"
            :class="{ 'tw-text-left !tw-left-0 !tw-relative !tw-top-0': isAsiInquiry }"
        />
        <Heading
            title="Für wen dürfen wir unverbindlich planen?"
            :size="2"
            :class="{ '!tw-text-[32px] !tw-leading-[1.1] !tw-mt-8': isAsiInquiry }"
        />
        <gender-selector />
        <c-t-a-fields :inquiry-type="travelData.inquiryType" />
        <custom-button @click="routeChange()" :disabled="!formValidity || pending">
            {{ pending ? 'Wird gesendet...' : 'Unverbindliche Anfrage senden &rarr;' }}
        </custom-button>
    </div>
</template>

<script>
import CryptoJS from 'crypto-js';
import { mapGetters, mapActions } from 'vuex';
import { getMarketingContext } from "../utils/marketingContext.js";
import CustomButton from "../components/baseComponents/CustomButton.vue";
import GenderSelector from "../components/userDataComponents/GenderSelector.vue";
import CTAFields from "../components/userDataComponents/CTAFields.vue";
import AcquisitionFormProgress from "../components/baseComponents/AcquisitionFormProgress.vue";
import { 
    calculateMessageQuality, 
    calculateBudgetQuality, 
    calculatePeopleQuality, 
    calculateTourQuality, 
    calculateTotalQuality 
} from "../utils/qualityCalculator.js";

export default {
    name: 'UserData',
    components: {
        AcquisitionFormProgress, 
        CTAFields, 
        GenderSelector, 
        CustomButton,
    },
    data() {
        return {
            mailchimpError: '',
            mailchimpSuccess: false,
        };
    },
    created() {
        if(localStorage.getItem('travelData') && !this.travelData.user.email) {
            const storedData = JSON.parse(localStorage.getItem('travelData'))
            // Only load the stored data if the current route matches either the
            // stored tour or country and if the store doesn't already have user data.
            if (
                this.tour && storedData.tour === this.tour.uid ||
                this.country && storedData.destinationCountry === this.country.uid
            ) {
                this.$store.commit('tour/setTravelData', storedData);
            }
        }
    },
    mounted() {
        this.$store.commit('tour/setQualityValue', this.qualityValue)
    },
    methods: {
        ...mapActions({
            submitForm: 'tour/submitForm',
        }),
        hashEmail(email) {
            const normalizedEmail = email.trim().toLowerCase();
            const hash = CryptoJS.SHA256(normalizedEmail);
            return hash.toString();
        },
        async signupForMailchimp() {
            const { email, firstName, lastName } = this.travelData.user;
            try {
                const formData = new FormData();
                formData.append('EMAIL', email);
                formData.append('FNAME', firstName);
                formData.append('LNAME', lastName);
                formData.append('b_037f3a95f6de2d4cb9801a5e2_87338bf9e9', ''); // Honeypot field
                formData.append('tags', '6800017');
                formData.append('gdpr[87942]', 'Y'); // GDPR consent

                const response = await fetch('https://wedesigntrips.us17.list-manage.com/subscribe/post?u=037f3a95f6de2d4cb9801a5e2&id=87338bf9e9&v_id=627&f_id=007c79e0f0', {
                    method: 'POST',
                    mode: 'no-cors', // Important for CORS issues
                    body: formData
                });

                // Since we're using no-cors, we can't actually check the response status
                // We'll assume success if no error is thrown
                this.mailchimpSuccess = true;
            } catch (error) {
                console.error('Mailchimp signup error:', error);
                this.mailchimpError = 'There was an error signing up for the newsletter. Your form will still be submitted.';
            }
        },
        async routeChange() {
            if (this.pending) {
                return;
            } else {
                this.$store.commit('tour/setPending', true);
            }

            // in case the user is trying to cheat and removes the disabled prop of the button
            if (!this.formValidity) {
                alert('Bitte fülle alle Felder aus.')
                return
            }

            let inquiryResponse;
            try {
                // Perform Mailchimp signup if mailChimpSignup is true
                if (this.mailchimpSignup) {
                    await this.signupForMailchimp();
                }

                inquiryResponse = await this.submitForm(this.travelData)

                if (this.error) {
                    alert('Aufgrund eines technischen Problems konnte Deine Anfrage leider nicht abgeschickt werden. Bitte kontaktiere uns direkt unter info@wedesigntrips.com');
                    return
                }
                
                // reset all travelData on successful submission
                localStorage.removeItem('travelData');

                this.$gtm.trackEvent({
                    event: 'inquirySubmitted',
                    travelType: window.location.pathname.includes('reise-planen') ? 'country' : 'tour',
                    year: this.travelData.year,
                    month: this.travelData.fullMonthName,
                    duration: this.travelData.duration,
                    numPeople: this.travelData.groupSize.adults + this.travelData.groupSize.children + this.travelData.groupSize.infants,
                    budget: this.travelData.budget,
                    messageLength: this.travelData.message ? this.travelData.message.length : 0,
                    country: this.country ? this.country?.mainCountry.staticCountry.shortName : this.tour?.mainCountry.staticCountry.shortName,
                    inquiryQualityValue: this.travelData.qualityValue,
                    // normalize email and hash with sha256
                    email: this.hashEmail(this.travelData.user.email),
                    inquiryType: this.travelData.inquiryType,
                    source: this.isAsiInquiry ? 'ASI-Tailor-Made' : 'WDT',
                    destination: this.country ? this.country?.mainCountry.staticCountry.shortName : this.tour?.mainCountry.staticCountry.shortName,
                    ...getMarketingContext(),
                })
            } catch (error) {
                console.error('FormSubmitError:', error);
                alert('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
            } finally {
                // Use the response data directly for router navigation
                const hasExistingBookings = inquiryResponse?.hasExistingBookings;
                this.$router.push({ 
                    name: 'success', 
                    params: hasExistingBookings !== undefined ? { hasExistingBookings } : {}
                });
            }
        },
    },
    computed: {
        ...mapGetters({
            tour: 'tour/route',
            country: 'tour/country',
            inquiry: 'tour/inquiry',
            travelData: 'tour/travelData',
            isAsiInquiry: 'tour/isAsiInquiry',
            mailchimpSignup: 'tour/mailchimpSignup',
            error: 'tour/error',
            pending: 'tour/pending',
            phoneValid: 'tour/phoneValid',
            emailValid: 'tour/emailValid',
            advisedBudget: 'tour/advisedBudget',
        }),
        formValidity() {
            const isPhoneInquiry = this.travelData.inquiryType === 'phone-inquiry'
            
            return !!(
                this.travelData.user &&
                this.travelData.user.firstName &&
                this.travelData.user.lastName &&
                this.travelData.user.email &&
                this.emailValid &&
                (!isPhoneInquiry || this.phoneValid) &&
                this.travelData.user.gender &&
                this.travelData.tos
            );
        },
        qualityValue() {
            // if quality value is above threshold, the request will be assigned automatically
            const debug = process.env.APP_ENV === 'dev';
            const isCountry = !!this.country;

            // Calculate message quality (if null, return 0 for entire quality)
            const messageQuality = calculateMessageQuality(this.travelData.message, isCountry);
            if (messageQuality === null) {
                if (debug) console.log("No message provided");
                return 0;
            }
            
            // If message is excellent, return max quality immediately
            if (messageQuality === 100) {
                if (debug) console.log("Excellent message quality");
                return 100;
            }

            // Parse budget if needed
            let budget
            if (typeof this.travelData.budget === 'string') {
                budget = this.travelData.budget.split(',').map(Number);
            } else {
                budget = this.travelData.budget;
            }
            
            // Use advisedBudget from store (includes travelingAloneFactor logic)
            const advisedBudget = this.advisedBudget;
            
            // WDT-1741 if request is from route, budget is more than advisedBudget and message is not empty -> assign the request automatically
            if (this.tour?.routeAdvisedBudgetPerDay && budget[1] > advisedBudget && this.travelData.message.length > 0) {
                return 100;
            }
            
            // Calculate quality components
            const budgetQuality = calculateBudgetQuality(
                budget[1], 
                advisedBudget
            );
            const peopleQuality = calculatePeopleQuality(this.travelData.groupSize);
            const tourQuality = calculateTourQuality(this.tour);
            
            // Calculate total quality
            return calculateTotalQuality(
                messageQuality, 
                budgetQuality, 
                peopleQuality, 
                tourQuality,
                debug
            );
        }
    },
}
</script>

<style scoped>
</style>